// ============================================================
// settlementUi.tsx — small pieces shared by the case's Settlement section
// (Stage 4 round 2a). Builds on the billing kit (billingUi: CARD, MoneyInput,
// INPUT, LABEL …) so the section reads like Billing.
// ============================================================
import React, { useEffect, useId, useState } from 'react'
import { MoneyInput, parsePoundsStrict, penceToPoundsText, LABEL, NOTE, INPUT, FIELD_ERROR, BIcon, Skel } from '../billing/billingUi'
import { SIcon, type SettingsIconName } from '../settings/settingsUi'
import ActionBox from '../shared/ActionBox'
import { SUCCESS_FEE_BASIS_LABEL, SUCCESS_FEE_BASIS_NOTE, SUCCESS_FEE_BASIS_OPTIONS, bpToPercentText, percentTextToBp } from '../../types/settlement.types'

// A money field bound to PENCE. Keeps its own text while typing; reports
// pence only when the text is a valid amount (blank → 0, or null when
// allowBlank). STRICT (parsePoundsStrict): nothing typed is stripped or
// rounded — "-5", "1e3", "1.005" or an amount over the server maximum show
// an error under the field and are never sent. `error` (e.g. the server's
// refusal of a save) wins over the local one; `warning` is amber, not an error.
export const PenceField: React.FC<{
  label: string
  pence: number | null
  onPence: (p: number | null) => void
  allowBlank?: boolean
  hint?: React.ReactNode
  warning?: React.ReactNode
  error?: string | null
  disabled?: boolean
  onBadChange?: (bad: boolean) => void
  // 'row' = the Settlement figures layout: label (+ hint) on the left, a
  // fixed 156px money box on the right, notes full-width underneath.
  layout?: 'stack' | 'row'
}> = ({ label, pence, onPence, allowBlank, hint, warning, error, disabled, onBadChange, layout = 'stack' }) => {
  const id = useId()
  const { text, change, bad } = usePenceText(pence, onPence, allowBlank, onBadChange)
  const err = error ?? bad
  // Row layout: the error sits full-width under the row (not squeezed under
  // the 156px box), so the box is tied to it here — invalid + described by it.
  const noteId = `${id}-note`
  useEffect(() => {
    if (layout !== 'row') return
    const input = document.getElementById(id)
    if (!input) return
    if (err) { input.setAttribute('aria-invalid', 'true'); input.setAttribute('aria-describedby', noteId) }
    else { input.removeAttribute('aria-invalid'); input.removeAttribute('aria-describedby') }
  }, [layout, id, noteId, err])
  if (layout === 'row') {
    return (
      <FieldRow label={label} htmlFor={id} hint={hint}
        note={err ? <span id={noteId} className={FIELD_ERROR} role="alert">{err}</span> : warning ? <span className="block text-[12px] text-casevo-warn mt-[4px]" role="status">{warning}</span> : null}>
        <MoneyInput id={id} strict value={text} onChange={change} disabled={disabled} placeholder={allowBlank ? '—' : '0.00'}
          className={`w-[156px] ${err ? '[&_input]:!border-casevo-danger' : ''}`} />
      </FieldRow>
    )
  }
  return (
    <div className="min-w-0">
      <label className={LABEL} htmlFor={id}>{label}</label>
      <MoneyInput id={id} strict value={text} onChange={change} error={err} disabled={disabled} placeholder={allowBlank ? '—' : '0.00'} />
      {!err && warning && <p className="text-[12px] text-casevo-warn mt-[5px]" role="status">{warning}</p>}
      {!err && !warning && hint && <p className={`${NOTE} mt-[5px]`}>{hint}</p>}
    </div>
  )
}

// (Audit M11) Which PenceFields hold text that isn't a valid amount (their
// value is still the OLD one): Save / Record … wait until none do.
//   const bad = useBadFields()
//   <PenceField … onBadChange={bad.track('fee')} />   …   disabled={bad.any}
export const useBadFields = (): { track: (key: string) => (b: boolean) => void; any: boolean; reset: () => void } => {
  const [set, setSet] = useState<Record<string, boolean>>({})
  const track = (key: string) => (b: boolean): void => setSet((s) => (!!s[key] === b ? s : { ...s, [key]: b }))
  return { track, any: Object.values(set).some(Boolean), reset: () => setSet({}) }
}

// The strict money text ↔ pence binding behind PenceField (both layouts).
// Keeps its own text while typing; reports pence only for a valid amount.
export const usePenceText = (
  pence: number | null, onPence: (p: number | null) => void, allowBlank?: boolean, onBadChange?: (bad: boolean) => void,
): { text: string; change: (t: string) => void; bad: string | null } => {
  // Zero shows blank (the 0.00 placeholder) unless blank means "none".
  const toText = (p: number | null): string => (p == null || (p === 0 && !allowBlank) ? '' : penceToPoundsText(p))
  const [text, setText] = useState(() => toText(pence))
  const [bad, setBad] = useState<string | null>(null)
  const setBadMsg = (m: string | null): void => { setBad(m); onBadChange?.(m !== null) }
  // Follow an outside change (load / reload) without fighting the typist.
  useEffect(() => {
    const r = parsePoundsStrict(text)
    const parsed = r.ok ? r.pence : r.blank ? (allowBlank ? null : 0) : undefined
    if (parsed !== pence) { setText(toText(pence)); setBadMsg(null) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pence])
  const change = (t: string): void => {
    setText(t)
    const r = parsePoundsStrict(t)
    if (r.ok) { setBadMsg(null); onPence(r.pence); return }
    if (r.blank) { setBadMsg(null); onPence(allowBlank ? null : 0); return }
    setBadMsg(r.error)
  }
  return { text, change, bad }
}

// One figures row (mockup option A): label + optional hint on the left, the
// control (fixed width) on the right, any note full-width underneath. Every
// row in a card shares the same two-column grid, so labels and boxes align.
export const FieldRow: React.FC<{ label: React.ReactNode; htmlFor?: string; hint?: React.ReactNode; note?: React.ReactNode; children: React.ReactNode }> = ({ label, htmlFor, hint, note, children }) => (
  <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-[16px] items-center py-[5px]">
    <div className="min-w-0">
      {htmlFor ? <label htmlFor={htmlFor} className="block text-[13.5px] text-casevo-forest leading-snug">{label}</label> : <span className="block text-[13.5px] text-casevo-forest leading-snug">{label}</span>}
      {hint && <span className="block text-[12px] text-casevo-text-soft leading-snug mt-[1px]">{hint}</span>}
    </div>
    <div className="justify-self-end">{children}</div>
    {note && <div className="col-span-2 min-w-0">{note}</div>}
  </div>
)

// A figures section card (mockup option A): icon tile, title + one-line
// sub, and the section's SUBTOTAL on the right of the header.
// (Batch 2, read-first) `editing` rings the card like every explicit-save
// card (ClaimCard CARD_EDITING), and `footer` is its Save / Cancel bar.
export const FigureCard: React.FC<{
  icon: SettingsIconName; title: string; sub?: React.ReactNode
  total?: React.ReactNode; totalLabel?: string; right?: React.ReactNode
  label?: string; children: React.ReactNode
  editing?: boolean; footer?: React.ReactNode; id?: string
  // (Audit fix batch 1, M5) Open but locked meanwhile: every input disabled,
  // the typed values still shown.
  readOnly?: boolean
}> = ({ icon, title, sub, total, totalLabel, right, label, children, editing, footer, id, readOnly }) => (
  <section id={id} className={`bg-casevo-surface border border-casevo-border rounded-[14px] shadow-[0_1px_2px_rgba(16,42,29,0.04),0_6px_18px_rgba(16,42,29,0.06)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.25),0_4px_14px_rgba(0,0,0,0.18)] ${editing ? `${CARD_EDITING_RING} overflow-hidden` : ''}`} aria-label={label ?? title}>
    <div className="flex items-start gap-3 px-5 pt-4 pb-2">
      <div className="w-[34px] h-[34px] rounded-[10px] bg-casevo-mint text-casevo-brand dark:text-casevo-link grid place-items-center shrink-0"><SIcon name={icon} size={16} /></div>
      <div className="flex-1 min-w-0">
        <h3 className="m-0 text-[15.5px] font-semibold text-casevo-forest">{title}</h3>
        {sub && <p className="m-0 mt-[2px] text-[12.5px] text-casevo-text-soft">{sub}</p>}
      </div>
      {(total != null || right) && (
        <div className="flex items-center gap-[10px] shrink-0 pt-[6px]">
          {right}
          {total != null && (
            <span className="text-right whitespace-nowrap">
              <b className="text-[14px] font-semibold tabular-nums text-casevo-forest">{total}</b>
              {totalLabel && <span className="ml-[6px] text-[12px] text-casevo-text-soft">{totalLabel}</span>}
            </span>
          )}
        </div>
      )}
    </div>
    <div className="px-5 pb-4">{readOnly ? <fieldset disabled className="m-0 p-0 border-0 min-w-0">{children}</fieldset> : children}</div>
    {footer}
  </section>
)
// The editing ring (the same values as ClientInfoSections CARD_EDITING).
const CARD_EDITING_RING =
  '!border-casevo-mid !shadow-[0_0_0_3px_color-mix(in_srgb,var(--casevo-mid)_18%,transparent),0_1px_2px_rgba(16,42,29,0.04),0_6px_18px_rgba(16,42,29,0.06)] ' +
  'dark:!shadow-[0_0_0_3px_color-mix(in_srgb,var(--casevo-mid)_18%,transparent),0_1px_2px_rgba(0,0,0,0.25),0_4px_14px_rgba(0,0,0,0.18)]'

// (Batch 2, read-first) A saved value in a card's READ view: the same row
// grid as the inputs, the value as plain text on the right.
export const ReadRow: React.FC<{ label: React.ReactNode; hint?: React.ReactNode; note?: React.ReactNode; children: React.ReactNode }> = ({ label, hint, note, children }) => (
  <FieldRow label={label} hint={hint} note={note}>
    <span className="block min-h-[28px] leading-[28px] text-[14px] tabular-nums text-casevo-forest text-right">{children}</span>
  </FieldRow>
)

// The pencil on a read-first card (not shown while the figures are locked).
// (Audit fix batch 2, M8) A disabled pencil stays focusable (aria-disabled)
// and explains itself: `describedBy` points at a VISIBLE line saying why
// (e.g. "Save or cancel the Costs card first."), not only a hover title.
export const EditPencil: React.FC<{ title: string; onClick: () => void; disabled?: boolean; disabledReason?: string; describedBy?: string }> = ({ title, onClick, disabled, disabledReason, describedBy }) => (
  <button type="button" onClick={() => { if (!disabled) onClick() }} aria-disabled={disabled || undefined} aria-label={`Edit ${title}`} title={disabled ? disabledReason : `Edit ${title}`}
    aria-describedby={disabled ? describedBy : undefined} data-edit-pencil={title}
    className={`w-[30px] h-[30px] rounded-[8px] grid place-items-center text-casevo-text-soft transition-colors focus:outline-none casevo-focus ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-casevo-mint hover:text-casevo-brand dark:hover:text-casevo-link'}`}>
    <BIcon name="pen" size={15} />
  </button>
)

// An editing card's footer (the ClaimCard pattern): Cancel discards, Save
// sends the card's changed fields in ONE request; while any field is invalid
// Save is disabled with "Fix the highlighted fields to save.".
export const FIX_TO_SAVE = 'Fix the highlighted fields to save.'
const B_SAVE_SM = 'inline-flex items-center gap-[6px] h-[30px] px-[11px] rounded-[8px] text-[12.5px] font-semibold border border-casevo-primary bg-casevo-primary text-white enabled:hover:bg-casevo-primary-hover whitespace-nowrap transition-colors focus:outline-none casevo-focus disabled:opacity-45 disabled:cursor-not-allowed'
const B_CANCEL_SM = 'inline-flex items-center h-[30px] px-[11px] rounded-[8px] text-[12.5px] font-medium border border-casevo-border bg-casevo-surface text-casevo-forest enabled:hover:border-casevo-mid whitespace-nowrap transition-colors focus:outline-none casevo-focus disabled:opacity-45 disabled:cursor-not-allowed'
export const EditFooter: React.FC<{ blocked: boolean; saving: boolean; error?: string | null; onCancel: () => void; onSave: () => void }> = ({ blocked, saving, error, onCancel, onSave }) => (
  <div className="flex items-center gap-2 justify-end flex-wrap py-[10px] px-[14px] bg-casevo-input border-t border-casevo-border-soft">
    <span className="mr-auto text-[12px] text-casevo-text-soft flex items-center gap-1.5">
      <i className="w-[7px] h-[7px] rounded-full bg-casevo-mid" aria-hidden="true" />Nothing saves until you press Save · Cancel discards
    </span>
    {/* (Audit fix batch 1, Low 13) Shown, not announced — it can appear while a date is still being typed. */}
    {blocked && <span className="text-[12px] text-casevo-danger">{FIX_TO_SAVE}</span>}
    {error && <span role="alert" className="text-[12px] text-casevo-danger">{error}</span>}
    <button type="button" className={B_CANCEL_SM} onClick={onCancel} disabled={saving}>Cancel</button>
    <button type="button" className={B_SAVE_SM} onClick={onSave} disabled={blocked || saving}>
      <BIcon name="check" size={13} />{saving ? 'Saving…' : 'Save'}
    </button>
  </div>
)

// (Audit fix batch 1, M5) The open card's footer once a statement locked the
// figures: why it can't be saved, and Cancel (the typed values stay visible
// above until then, so they can be noted down).
export const LockedFooter: React.FC<{ version: number; stage: 'PREPARED' | 'APPROVED'; onCancel: () => void }> = ({ version, stage, onCancel }) => (
  // (D12) A plain divider on the card's white — no tinted band behind the button; the amber lock carries the warning.
  <div className="flex items-center gap-x-3 gap-y-2 justify-end flex-wrap py-[10px] px-[14px] bg-casevo-surface border-t border-casevo-border-soft" data-locked-footer="">
    <span className="mr-auto flex-[1_1_240px] min-w-0 text-[12.5px] text-casevo-forest flex items-start gap-1.5" role="status">
      <span className="shrink-0 mt-[2px] text-casevo-warn"><BIcon name="lock" size={13} /></span>
      <span>Statement v{version} was {stage === 'APPROVED' ? 'approved' : 'prepared'} while you were editing, so these figures can’t be saved. What you typed is shown above — note it down, then Cancel. To change the figures, send the statement back for changes first.</span>
    </span>
    <button type="button" className={`${B_CANCEL_SM} min-h-[44px] sm:min-h-[30px]`} onClick={onCancel}>Cancel</button>
  </div>
)

// ⓘ — a small info button with the LEGAL reference in a tooltip (hover /
// focus; Esc closes). Keeps the setting's own line in plain English.
export const InfoTip: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
  const [open, setOpen] = useState(false)
  const tipId = useId()
  return (
    <span className="relative inline-flex align-middle">
      <button type="button" aria-label={label} aria-describedby={open ? tipId : undefined}
        onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false) } }}
        className="w-[16px] h-[16px] rounded-full border border-casevo-text-soft text-casevo-text-soft text-[10px] font-bold leading-none grid place-items-center hover:text-casevo-forest hover:border-casevo-forest focus:outline-none casevo-focus">
        i
      </button>
      {open && (
        <span id={tipId} role="tooltip" className="absolute z-[90] left-1/2 -translate-x-1/2 top-[22px] w-[300px] max-w-[70vw] rounded-[8px] bg-casevo-header text-white text-[12px] leading-[1.45] px-[12px] py-[9px] shadow-[0_10px_30px_rgba(0,0,0,0.25)]">
          <span className="block text-[10px] font-bold tracking-[0.12em] uppercase text-casevo-light mb-[3px]">The legal bit</span>
          {children}
        </span>
      )}
    </span>
  )
}

// A field-shaped placeholder: label line + a 38px input well, the same
// footprint as PenceField / a text box / a select (LABEL 12px + 5px gap).
export const FieldSkel: React.FC = () => (
  <div aria-hidden="true"><Skel className="h-[13px] w-28 mb-[5px]" /><Skel className="h-[38px] w-full" /></div>
)

// "DRAFT — not for client use": on the preview header and in the net tile.
export const DraftBadge: React.FC<{ onDark?: boolean }> = ({ onDark }) => (
  <span className={`inline-flex items-center text-[10.5px] font-bold uppercase tracking-[0.08em] px-2 py-[3px] rounded-[5px] border ${onDark ? 'border-white/40 bg-white/[0.14] text-white' : 'border-casevo-warn-border bg-casevo-warn-bg text-casevo-warn'}`}>
    Draft — not for client use
  </span>
)

// A percentage field bound to BASIS POINTS (blank → null).
export const PercentField: React.FC<{
  label: string
  bp: number | null
  onBp: (bp: number | null) => void
  hint?: React.ReactNode
  disabled?: boolean
  placeholder?: string
  // Told when the text turns invalid / valid — so a half-typed "40.555"
  // still counts as an unsaved change (the leave warning).
  onBadChange?: (bad: boolean) => void
}> = ({ label, bp, onBp, hint, disabled, placeholder = '—', onBadChange }) => {
  const id = useId()
  const [text, setText] = useState(() => bpToPercentText(bp))
  const [bad, setBadState] = useState(false)
  const setBad = (b: boolean): void => { setBadState(b); onBadChange?.(b) }
  useEffect(() => {
    const parsed = percentTextToBp(text)
    if (parsed !== bp) { setText(bpToPercentText(bp)); setBad(false) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bp])
  return (
    <div className="min-w-0">
      <label className={LABEL} htmlFor={id}>{label}</label>
      <div className="flex items-center gap-[8px]">
        <input id={id} className={`${INPUT} w-[110px] text-right tabular-nums ${bad ? '!border-casevo-danger' : ''}`} value={text} inputMode="decimal" autoComplete="off" disabled={disabled} placeholder={placeholder}
          aria-invalid={bad || undefined}
          onChange={(e) => {
            const t = e.target.value
            setText(t)
            const v = percentTextToBp(t)
            if (v === undefined) { setBad(true); return }
            setBad(false); onBp(v)
          }} />
        <span className="text-casevo-text-soft" aria-hidden="true">%</span>
      </div>
      {bad ? <span className={FIELD_ERROR} role="alert">Enter a percentage from 0 to 100.</span> : hint && <p className={`${NOTE} mt-[5px]`}>{hint}</p>}
    </div>
  )
}

// Amber (warning) / red (blocking) / info notes. (Audit fix batch 2, 31 Oct)
// Drawn with the shared ActionBox shell (white card, accent strip, icon chip):
// a short title (optional), the facts, and the action at the right of the bar.
// An error interrupts (role=alert); the others are polite statuses, as before.
export const Banner: React.FC<{ tone: 'warn' | 'error' | 'info'; children: React.ReactNode; action?: React.ReactNode; title?: React.ReactNode }> = ({ tone, children, action, title }) => (
  <ActionBox tone={tone === 'error' ? 'danger' : tone} title={title} message={children} alert={tone === 'error'} live={tone !== 'error'} footer={action} />
)

// "Success fee is calculated on" (Admin › PI settlements and the case's CFA card).
export const SuccessFeeBasisField: React.FC<{ recovered: boolean; onChange: (recovered: boolean) => void; disabled?: boolean; name: string }> = ({ recovered, onChange, disabled, name }) => (
  <fieldset className="m-0 p-0 border-0 py-[10px]" data-success-fee-basis>
    <legend className="p-0 text-[13.5px] font-medium text-casevo-forest">{SUCCESS_FEE_BASIS_LABEL}</legend>
    <div className="mt-[6px] flex flex-col gap-[6px]">
      {SUCCESS_FEE_BASIS_OPTIONS.map((o) => (
        <label key={o.label} className="inline-flex items-center gap-[8px] text-[13.5px] text-casevo-forest">
          <input type="radio" className="accent-casevo-primary" name={name} checked={recovered === o.recovered} disabled={disabled} onChange={() => onChange(o.recovered)} />
          {o.label}
        </label>
      ))}
    </div>
    <p className="m-0 mt-[6px] text-[12.5px] text-casevo-text-soft">{SUCCESS_FEE_BASIS_NOTE}</p>
  </fieldset>
)
